import React, { useCallback, useEffect, useState } from 'react';
import {
    Loader2, ChevronLeft, ChevronRight, RotateCw, FileX2, Link2Off, HelpCircle, X,
    Link2, Unlink, History, ExternalLink, ChevronDown, ChevronUp, Layers,
} from 'lucide-react';
import { useDocuWareFilingCheck, FILING_CHECK_PAGE_SIZE } from '../../../hooks/useDocuWareFilingCheck';
import { formatDate } from '../../../utils/format';
import { COMPANY_LABELS } from '../../../types/docuwareInvoices';
import { FILING_STATUS_LABELS } from '../../../types/docuwareFilingCheck';
import type {
    FilingCheckRow, FilingStatus, FilingCheckSortField, FilingCheckManualLink,
} from '../../../types/docuwareFilingCheck';
import { docuwareFilingCheckApi } from '../../../services/docuwareFilingCheckApi';
import { FilingCheckLinkDialog } from './FilingCheckLinkDialog';
import { useResizableColumns } from '../../../hooks/useResizableColumns';
import { ColumnResizeHandle } from '../../ui/ColumnResizeHandle';
import { DocuWareDocLink } from '../DocuWareDocLink';

interface Props {
    onCopyDocId: (docId: string) => void;
    /** No CanEdit, no buttons. The endpoint answers 403 either way; this stops the offer. */
    canEdit: boolean;
    onToast: (message: string, kind: 'success' | 'error') => void;
}

/**
 * What the audit says about THIS posting, in one sentence, or null.
 *
 * The visible half of Entscheid 2 (the plan, 2026-09-09): the matcher may replace a reviewer's
 * hand link when it re-matches that document on both keys, and Roman's condition for allowing
 * it at all was that the replacement is recorded AND shown. A posting sitting back on this
 * list with no explanation is the overnight disappearance the decision was written to prevent.
 */
function auditNote(r: FilingCheckRow): string | null {
    if (!r.auditAction || !r.auditActedAt) return null;
    const when = formatDate(r.auditActedAt.slice(0, 10));
    const who = r.auditLinkedBy ?? r.auditActedBy ?? 'someone';
    if (r.auditAction === 'replaced') {
        return `Hand link to document ${r.auditDocumentId} removed by the matcher on ${when}: `
            + `it matched that document to ${r.auditReplacedBy} on `
            + `${r.auditMatchMethod === 'both' ? 'both keys' : r.auditMatchMethod}. `
            + `Linked by ${who}${r.auditLinkedAt ? ` on ${formatDate(r.auditLinkedAt.slice(0, 10))}` : ''}.`;
    }
    if (r.auditAction === 'unlink') {
        return `Hand link to document ${r.auditDocumentId} removed by `
            + `${r.auditActedBy ?? 'a reviewer'} on ${when}.`;
    }
    return `Linked by hand to document ${r.auditDocumentId} by ${who} on ${when}.`;
}

const MATCH_BASIS_EXPLAIN: Record<string, string> = {
    exact: 'exact invoice-number match',
    normalised: 'matched after folding case and separators',
    contains: 'CONTAINMENT match - one number contains the other. Weaker: check the number and '
        + 'amount above before treating this as the same invoice.',
};

// 'collective' deliberately gets no warning colour (red/amber) — Roman's ask (P3): a legitimate
// Sammelbeleg fragment must not look like a defect at a glance. Slate reads as "informational",
// the same register as the grey 'nokey' state, not as something a reviewer needs to chase.
const STATUS_STYLE: Record<FilingStatus, { icon: typeof FileX2; cls: string }> = {
    missing: { icon: FileX2, cls: 'text-red-600' },
    unlinked: { icon: Link2Off, cls: 'text-amber-600' },
    collective: { icon: Layers, cls: 'text-slate-500' },
    nokey: { icon: HelpCircle, cls: 'text-gray-400' },
};

/** `id` is the identity of the stored column width; `sort` is what the server sorts on. */
interface FilingCol {
    id: string;
    label: string;
    sort?: FilingCheckSortField;
    align?: 'right';
    width: number;
}

const WIDTH_KEY = 'docuware-filing-check-col-widths';

const COLS: FilingCol[] = [
    { id: 'status', label: 'Status', sort: 'status', width: 150 },
    { id: 'company', label: 'Company', sort: 'company', width: 90 },
    { id: 'bcDocumentNo', label: 'BC posting', sort: 'bcDocumentNo', width: 150 },
    { id: 'postingDate', label: 'Posted', sort: 'postingDate', width: 105 },
    { id: 'documentDate', label: 'Invoice date', sort: 'documentDate', width: 110 },
    { id: 'vendorName', label: 'Supplier', sort: 'vendorName', width: 260 },
    { id: 'vendorInvoiceNo', label: 'Supplier invoice no.', sort: 'vendorInvoiceNo', width: 200 },
    { id: 'orderNo', label: 'Order', sort: 'orderNo', width: 130 },
    { id: 'netAmount', label: 'Net amount', sort: 'netAmount', align: 'right', width: 130 },
    // Only ever filled for a "filed, not linked" row: what was found in DocuWare, and where
    // that document is linked instead. This is the column that tells a matcher problem from
    // a filing problem without opening anything.
    { id: 'found', label: 'Found in DocuWare', width: 260 },
    { id: 'document', label: 'Document', width: 100 },
    { id: 'link', label: 'Link', width: 110 },
];

/**
 * "Filing check" — posted purchase invoices with no DocuWare document.
 *
 * The direction that was missing: the other two tabs start in DocuWare, this one starts in
 * Business Central. Heike's point 4.
 *
 * The states stay apart everywhere — in the tiles, in the status column, in the filter.
 * "Not in DocuWare" is a filing gap somebody has to chase; "Filed, not linked" is a matcher
 * gap that needs no chasing at all; "Sammelbeleg" (P3) is neither — a normal fragment of an
 * invoice that IS filed, kept out of the warning colours on purpose. A reviewer who cannot
 * tell them apart stops trusting the list after the first false alarm.
 */
export function FilingCheckTab({ onCopyDocId, canEdit, onToast }: Props) {
    const {
        rows, summary, total, loading, error,
        filters, setFilters, clearFilters, activeFilterCount,
        sortBy, sortOrder, setSort,
        page, setPage, totalPages, reload,
    } = useDocuWareFilingCheck(true);

    const { widthOf, totalWidthOf, resizingKey, startResize, resetWidth, shouldSuppressClick } =
        useResizableColumns(COLS.map(c => ({ key: c.id, width: c.width })), WIDTH_KEY);
    const totalWidth = totalWidthOf(COLS.map(c => c.id));

    // Counted over the whole corpus by the endpoint, never through the filters: this is the
    // number that gets reconciled against a hand-measured count.
    const countOf = (status: FilingStatus) => summary.byCompany
        .filter(c => c.status === status && (!filters.companyCode || c.companyCode === filters.companyCode))
        .reduce((sum, c) => sum + c.docs, 0);
    const zeroOf = (status: FilingStatus) => summary.byCompany
        .filter(c => c.status === status && (!filters.companyCode || c.companyCode === filters.companyCode))
        .reduce((sum, c) => sum + c.zeroDocs, 0);
    // How much of "filed, not linked" rests on the weaker containment match. Said out loud on
    // the tile, because a number whose evidence differs in kind should not look uniform.
    const containsOf = (status: FilingStatus) => summary.byCompany
        .filter(c => c.status === status && (!filters.companyCode || c.companyCode === filters.companyCode))
        .reduce((sum, c) => sum + c.containsDocs, 0);

    const postedTotal = Object.entries(summary.postedTotals)
        .filter(([code]) => !filters.companyCode || code === filters.companyCode)
        .reduce((sum, [, n]) => sum + n, 0);

    const statuses: FilingStatus[] = ['missing', 'unlinked', 'collective', 'nokey'];

    // --- the hand link (P2) -------------------------------------------------------------
    const [linking, setLinking] = useState<FilingCheckRow | null>(null);
    const [manualLinks, setManualLinks] = useState<FilingCheckManualLink[]>([]);
    const [linksOpen, setLinksOpen] = useState(false);
    const [removing, setRemoving] = useState<string | null>(null);

    // Loaded whether or not the panel is open: the COUNT belongs on the toggle, and a count
    // that only appears after you open the thing it counts is no use for deciding to open it.
    const loadManualLinks = useCallback(async () => {
        try {
            setManualLinks(await docuwareFilingCheckApi.manualLinks());
        } catch {
            // A failure here must not take the exception list down with it - the panel is an
            // aid, the list is the control.
            setManualLinks([]);
        }
    }, []);

    useEffect(() => { void loadManualLinks(); }, [loadManualLinks]);

    const afterWrite = async (message: string) => {
        setLinking(null);
        onToast(message, 'success');
        await Promise.all([reload(), loadManualLinks()]);
    };

    const removeLink = async (link: FilingCheckManualLink) => {
        const key = `${link.companyCode}-${link.bcDocumentNo}-${link.documentId}`;
        setRemoving(key);
        try {
            const result = await docuwareFilingCheckApi.unlink(
                link.companyCode, link.bcDocumentNo, link.documentId
            );
            await afterWrite(
                `${link.bcDocumentNo} unlinked from document ${link.documentId}`
                + (result.orderCheckRefreshed === null
                    ? ' · the order check could not be re-run (logged)'
                    : ` · order check re-run for ${result.orderCheckRefreshed} document(s)`)
            );
        } catch (err) {
            onToast(err instanceof Error ? err.message : 'Unlinking failed', 'error');
        } finally {
            setRemoving(null);
        }
    };

    return (
        <div className="space-y-4">
            <div className="flex flex-wrap gap-2">
                {statuses.map(s => {
                    const style = STATUS_STYLE[s];
                    const Icon = style.icon;
                    const active = filters.status === s;
                    const n = countOf(s);
                    // 'No invoice number' is a state that is usually empty. An empty tile
                    // that stays visible is the honest form: it says the case is checked for.
                    return (
                        <button
                            key={s}
                            onClick={() => setFilters({ status: active ? '' : s })}
                            title={FILING_STATUS_LABELS[s].explain}
                            className={`inline-flex items-center gap-2 rounded border px-3 py-2 text-sm transition-colors ${
                                active ? 'border-blue-500 bg-blue-50' : 'border-gray-200 bg-white hover:bg-gray-50'
                            }`}
                        >
                            <Icon className={`h-4 w-4 ${style.cls}`} />
                            <span className="text-gray-700">{FILING_STATUS_LABELS[s].label}</span>
                            <span className="font-semibold text-gray-900">{n.toLocaleString('de-CH')}</span>
                            {(zeroOf(s) > 0 || containsOf(s) > 0) && (
                                <span className="text-xs text-gray-400">
                                    {[
                                        zeroOf(s) > 0 ? `${zeroOf(s).toLocaleString('de-CH')} at 0.00` : null,
                                        containsOf(s) > 0 ? `${containsOf(s).toLocaleString('de-CH')} loose` : null,
                                    ].filter(Boolean).join(' · ')}
                                </span>
                            )}
                        </button>
                    );
                })}
            </div>

            <div className="flex flex-wrap items-end gap-3">
                <label className="block">
                    <span className="mb-1 block text-xs font-medium text-gray-600">Search</span>
                    <input
                        value={filters.search}
                        onChange={e => setFilters({ search: e.target.value })}
                        placeholder="Supplier, invoice no., BC no. or order no."
                        className="h-9 w-72 rounded border border-gray-300 px-2 text-sm"
                    />
                </label>

                <label className="block">
                    <span className="mb-1 block text-xs font-medium text-gray-600">Company</span>
                    <select
                        value={filters.companyCode}
                        onChange={e => setFilters({ companyCode: e.target.value })}
                        className="h-9 w-48 rounded border border-gray-300 px-2 text-sm"
                    >
                        <option value="">All companies</option>
                        {summary.companies.map(c => (
                            <option key={c} value={c}>{COMPANY_LABELS[c] ?? c}</option>
                        ))}
                    </select>
                </label>

                {/* Opt-in, never the default. Half the list is free samples, corrections and
                    reversals that legitimately never had a supplier invoice — hiding them
                    silently would make the count unreconcilable against any measurement. */}
                <label className="flex h-9 items-center gap-2 text-sm text-gray-700">
                    <input
                        type="checkbox"
                        checked={filters.amount === 'nonzero'}
                        onChange={e => setFilters({ amount: e.target.checked ? 'nonzero' : '' })}
                        className="h-4 w-4 rounded border-gray-300"
                    />
                    Hide postings at 0.00
                </label>

                {activeFilterCount > 0 && (
                    <button
                        onClick={clearFilters}
                        className="inline-flex h-9 items-center gap-1 rounded border border-gray-300 px-3 text-sm text-gray-600 hover:bg-gray-50"
                    >
                        <X className="h-4 w-4" /> Clear filters
                    </button>
                )}

                <button
                    onClick={() => void reload()}
                    disabled={loading}
                    className="inline-flex h-9 items-center gap-2 rounded border border-gray-300 px-3 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                    title="Re-run the query against Business Central and DocuWare."
                >
                    <RotateCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
                    Reload
                </button>
            </div>

            <div className="flex flex-wrap items-center gap-3 text-sm text-gray-500">
                <span>{total.toLocaleString('de-CH')} posting{total === 1 ? '' : 's'} shown</span>
                <span className="text-gray-300">·</span>
                <span>
                    out of {postedTotal.toLocaleString('de-CH')} purchase invoices posted since{' '}
                    {summary.minPostingDate ? formatDate(summary.minPostingDate) : '—'}
                </span>
            </div>

            {error && (
                <div className="rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                    {error}
                </div>
            )}

            {/* THE UNDO PATH, and it has to be here rather than in the table: a linked posting
                has LEFT the exception list by design, so without this panel a hand link could
                only ever be taken back inside the same page session. */}
            {manualLinks.length > 0 && (
                <div className="rounded-lg border border-gray-200 bg-white">
                    <button
                        onClick={() => setLinksOpen(o => !o)}
                        className="flex w-full items-center gap-2 px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50"
                    >
                        {linksOpen ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                        <History className="h-4 w-4 text-gray-400" />
                        <span className="font-medium">Manual links</span>
                        <span className="text-gray-500">{manualLinks.length}</span>
                        <span className="text-xs text-gray-400">
                            postings a reviewer linked by hand — they no longer appear above
                        </span>
                    </button>
                    {linksOpen && (
                        <div className="max-h-64 overflow-auto border-t border-gray-100">
                            <table className="w-full text-sm">
                                <tbody>
                                    {manualLinks.map(l => {
                                        const key = `${l.companyCode}-${l.bcDocumentNo}-${l.documentId}`;
                                        return (
                                            <tr key={key} className="border-b border-gray-50">
                                                <td className="px-4 py-2 font-mono text-xs text-gray-800">
                                                    {l.bcDocumentNo}
                                                    <span className="ml-1 text-gray-400">{l.companyCode}</span>
                                                </td>
                                                <td className="max-w-[16rem] truncate px-3 py-2 text-xs text-gray-600"
                                                    title={l.supplier ?? undefined}>
                                                    {l.supplier ?? '—'}
                                                </td>
                                                <td className="px-3 py-2 font-mono text-xs text-gray-600">
                                                    doc {l.documentId} · {l.invoiceNumber ?? '—'}
                                                </td>
                                                <td className="px-3 py-2 text-xs text-gray-500">
                                                    {/* Named because it changes what the Order Check
                                                        compares against, not because it is pretty. */}
                                                    {l.postingCount > 1
                                                        ? `collective — ${l.bcPostings}`
                                                        : 'only posting of that document'}
                                                </td>
                                                <td className="px-3 py-2 text-xs text-gray-500">
                                                    {l.createdBy ?? '—'}
                                                    {l.createdAt && ` · ${formatDate(l.createdAt.slice(0, 10))}`}
                                                </td>
                                                <td className="px-3 py-2">
                                                    {l.documentUrl && (
                                                        <a href={l.documentUrl} target="_blank" rel="noopener noreferrer"
                                                           className="inline-flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800">
                                                            open <ExternalLink className="h-3 w-3" />
                                                        </a>
                                                    )}
                                                </td>
                                                <td className="px-4 py-2 text-right">
                                                    {canEdit && (
                                                        <button
                                                            onClick={() => void removeLink(l)}
                                                            disabled={removing === key}
                                                            className="inline-flex items-center gap-1 rounded border border-gray-300 px-2 py-1 text-xs text-gray-700 hover:bg-gray-50 disabled:opacity-40"
                                                            title="Remove this hand link. The posting returns to the list above."
                                                        >
                                                            {removing === key
                                                                ? <Loader2 className="h-3 w-3 animate-spin" />
                                                                : <Unlink className="h-3 w-3" />}
                                                            Unlink
                                                        </button>
                                                    )}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            )}

            <div className="rounded-lg border border-gray-200 bg-white">
                {loading && rows.length === 0 ? (
                    <div className="flex h-48 items-center justify-center">
                        <Loader2 className="h-6 w-6 animate-spin text-blue-500" />
                    </div>
                ) : rows.length === 0 ? (
                    <div className="flex h-48 items-center justify-center px-6 text-center">
                        <p className="text-sm text-gray-500">
                            {activeFilterCount > 0
                                ? 'No postings match these filters.'
                                : 'Every purchase invoice posted since the cut-off has a DocuWare document.'}
                        </p>
                    </div>
                ) : (
                    // The scroll container the sticky header sticks inside of.
                    <div className="max-h-[70vh] overflow-auto">
                        <table className="w-full table-fixed text-sm" style={{ minWidth: totalWidth }}>
                            <colgroup>
                                {COLS.map(c => <col key={c.id} style={{ width: widthOf(c.id) }} />)}
                            </colgroup>
                            {/* Explicit background and an inset shadow instead of border-b — a
                                border on a sticky th does not travel with it. */}
                            <thead className="sticky top-0 z-20 bg-gray-50 text-xs uppercase tracking-wide shadow-[inset_0_-1px_0_rgb(229,231,235)]">
                                <tr>
                                    {COLS.map(c => (
                                        <th key={c.id}
                                            className={`relative px-3 py-2 font-medium text-gray-500 ${c.align === 'right' ? 'text-right' : 'text-left'}`}>
                                            {c.sort ? (
                                                <button
                                                    // Swallow the click that ends a resize drag,
                                                    // or every drag also re-sorts the table.
                                                    onClick={() => { if (!shouldSuppressClick()) setSort(c.sort!); }}
                                                    className="inline-flex max-w-full items-center gap-1 hover:text-gray-900"
                                                >
                                                    <span className="truncate">{c.label}</span>
                                                    {sortBy === c.sort && (
                                                        <span className="shrink-0 text-gray-400">{sortOrder === 'asc' ? '▲' : '▼'}</span>
                                                    )}
                                                </button>
                                            ) : c.label}
                                            <ColumnResizeHandle
                                                label={c.label || c.id}
                                                active={resizingKey === c.id}
                                                onStart={(x) => startResize(c.id, x)}
                                                onReset={() => resetWidth(c.id)}
                                            />
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {rows.map((r: FilingCheckRow) => {
                                    const style = STATUS_STYLE[r.status] ?? STATUS_STYLE.nokey;
                                    const Icon = style.icon;
                                    const note = auditNote(r);
                                    return (
                                    <React.Fragment key={`${r.companyCode}-${r.bcDocumentNo}`}>
                                        <tr className="border-b border-gray-100 hover:bg-gray-50">
                                            <td className="px-3 py-2">
                                                <span className="inline-flex items-center gap-1.5"
                                                      title={FILING_STATUS_LABELS[r.status]?.explain}>
                                                    <Icon className={`h-4 w-4 shrink-0 ${style.cls}`} />
                                                    <span className="truncate text-gray-700">
                                                        {FILING_STATUS_LABELS[r.status]?.label ?? r.status}
                                                    </span>
                                                    {/* The sibling count, on the line — the whole
                                                        point of a quiet status is that a reviewer
                                                        can see WHY without opening anything. */}
                                                    {r.status === 'collective' && r.dwPostingCount !== null && (
                                                        <span className="text-xs text-slate-400">
                                                            {r.dwPostingCount} posting{r.dwPostingCount === 1 ? '' : 's'}
                                                        </span>
                                                    )}
                                                </span>
                                            </td>
                                            <td className="px-3 py-2 font-medium text-gray-700">{r.companyCode}</td>
                                            <td className="truncate px-3 py-2 font-mono text-xs text-gray-700"
                                                title={r.bcDocumentNo}>
                                                {r.bcDocumentNo}
                                            </td>
                                            <td className="px-3 py-2 text-gray-600">
                                                {r.postingDate ? formatDate(r.postingDate) : '—'}
                                            </td>
                                            <td className="px-3 py-2 text-gray-600">
                                                {r.documentDate ? formatDate(r.documentDate) : '—'}
                                            </td>
                                            {/* table-fixed clips instead of pushing, so the full
                                                value stays reachable via title. */}
                                            <td className="truncate px-3 py-2 text-gray-700" title={r.vendorName ?? undefined}>
                                                {r.vendorName ?? '—'}
                                                {r.vendorNo && <span className="ml-1 text-xs text-gray-400">{r.vendorNo}</span>}
                                            </td>
                                            <td className="truncate px-3 py-2 font-mono text-xs text-gray-700"
                                                title={r.vendorInvoiceNo ?? undefined}>
                                                {r.vendorInvoiceNo ?? <span className="text-gray-400">—</span>}
                                            </td>
                                            <td className="truncate px-3 py-2 font-mono text-xs text-gray-600"
                                                title={r.orderNo ?? undefined}>
                                                {r.orderNo ?? <span className="text-gray-300">—</span>}
                                            </td>
                                            <td className="px-3 py-2 text-right text-gray-700">
                                                {r.netAmount === null ? (
                                                    <span className="text-gray-300">—</span>
                                                ) : (
                                                    <>
                                                        {r.netAmount.toLocaleString('de-CH', {
                                                            minimumFractionDigits: 2, maximumFractionDigits: 2,
                                                        })}
                                                        <span className="ml-1 text-xs text-gray-400">{r.currency}</span>
                                                    </>
                                                )}
                                            </td>
                                            <td className="truncate px-3 py-2 text-xs text-gray-600">
                                                {r.dwDocumentId ? (
                                                    <span title={[
                                                        r.dwSupplier,
                                                        r.dwAmount === null ? null : `${r.dwAmount.toLocaleString('de-CH', {
                                                            minimumFractionDigits: 2, maximumFractionDigits: 2,
                                                        })} ${r.dwCurrency ?? ''}`.trim(),
                                                        r.dwStoredAt ? `filed ${formatDate(r.dwStoredAt)}` : null,
                                                        MATCH_BASIS_EXPLAIN[r.matchBasis ?? 'exact'],
                                                        r.dwBcDocumentNo
                                                            ? `that document is linked to ${r.dwBcDocumentNo}`
                                                            : 'that document is not linked to any posting',
                                                    ].filter(Boolean).join(' · ')}>
                                                        {/* The found document's OWN number, not a
                                                            verdict: on a containment match it is
                                                            the one thing that lets a reviewer
                                                            reject the candidate at a glance. */}
                                                        <span className="font-mono">{r.dwInvoiceNumber}</span>
                                                        {r.matchBasis === 'contains' && (
                                                            <span className="ml-1 text-amber-600">(loose)</span>
                                                        )}
                                                        {r.matchBasis === 'normalised' && (
                                                            <span className="ml-1 text-gray-400">(separators)</span>
                                                        )}
                                                        <span className="ml-1 text-gray-400">
                                                            {r.dwBcDocumentNo ? `→ ${r.dwBcDocumentNo}` : '→ no BC link'}
                                                        </span>
                                                    </span>
                                                ) : (
                                                    <span className="text-gray-300">—</span>
                                                )}
                                            </td>
                                            <td className="px-3 py-2">
                                                {r.dwDocumentId ? (
                                                    <DocuWareDocLink row={r} onCopyDocId={onCopyDocId} />
                                                ) : (
                                                    <span className="text-xs text-gray-300">none</span>
                                                )}
                                            </td>
                                            {/* No CanEdit, no button. The endpoint answers 403
                                                either way, but offering an action that will be
                                                refused is its own kind of wrong. A 'missing' row
                                                keeps the button: the dialog's search reaches
                                                documents the key search never found, which is
                                                exactly what a missing row needs. */}
                                            <td className="px-3 py-2">
                                                {canEdit ? (
                                                    <button
                                                        onClick={() => setLinking(r)}
                                                        className="inline-flex items-center gap-1 rounded border border-gray-300 px-2 py-1 text-xs text-gray-700 hover:bg-gray-50"
                                                        title="Pick the DocuWare document this posting belongs to."
                                                    >
                                                        <Link2 className="h-3 w-3" /> Link
                                                    </button>
                                                ) : (
                                                    <span className="text-xs text-gray-300">—</span>
                                                )}
                                            </td>
                                        </tr>
                                        {note && (
                                            <tr className="border-b border-gray-100 bg-amber-50/50">
                                                <td colSpan={COLS.length} className="px-3 py-1.5">
                                                    <span className="inline-flex items-start gap-1.5 text-[11px] text-amber-800">
                                                        <History className="mt-px h-3.5 w-3.5 shrink-0" />
                                                        {note}
                                                    </span>
                                                </td>
                                            </tr>
                                        )}
                                    </React.Fragment>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {linking && (
                <FilingCheckLinkDialog
                    row={linking}
                    onClose={() => setLinking(null)}
                    onLinked={(message) => void afterWrite(message)}
                    onError={(message) => onToast(message, 'error')}
                />
            )}

            {total > FILING_CHECK_PAGE_SIZE && (
                <div className="flex items-center justify-between text-sm text-gray-600">
                    <span>Page {page} of {totalPages}</span>
                    <div className="flex items-center gap-2">
                        <button
                            onClick={() => setPage(Math.max(1, page - 1))}
                            disabled={page <= 1 || loading}
                            className="inline-flex h-8 items-center gap-1 rounded border border-gray-300 px-2 hover:bg-gray-50 disabled:opacity-40"
                        >
                            <ChevronLeft className="h-4 w-4" /> Previous
                        </button>
                        <button
                            onClick={() => setPage(Math.min(totalPages, page + 1))}
                            disabled={page >= totalPages || loading}
                            className="inline-flex h-8 items-center gap-1 rounded border border-gray-300 px-2 hover:bg-gray-50 disabled:opacity-40"
                        >
                            Next <ChevronRight className="h-4 w-4" />
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}
